import React, { useEffect } from 'react'
import { Row } from 'react-bootstrap'

import InlineLoader from '../../components/InlineLoader'
import {
  API_OPERATIONS,
  FEEDBACK,
  QUERY_SUGGESTIONS_MODE
} from './constants'
import { getDataSourceNameLookup } from './utils'
import { KendraStyledWrapper } from './styles'
import _ from 'lodash'

import { useKendra } from '../../hooks'

import PageIndex from './pagination/PageIndex'
import Pagination from './pagination/Pagination'
import ResultPanel from './ResultPanel'
import ResultSorting from './sorting/ResultsSorting'

const RESULTS_CLASSNAMES_WITH_FACETS = 'col-xs-9 col-sm-9 col-md-9 col-lg-9';
const RESULTS_CLASSNAMES_WITHOUT_FACETS = 'col-xs-12 col-sm-12 col-md-12 col-lg-12';
const FACETS_CLASSNAMES_WITH_FACETS = 'col-xs-3 col-sm-3 col-md-3 col-lg-3';
const FACETS_CLASSNAMES_WITHOUT_FACETS = 'd-none';

/**
 * Component for displaying search results using AWS Kendra.
 *
 * @todo Consider rendering the error notification at Subheader level
 * @see {@link https://github.com/aws-samples/aws-kendra-transcribe-media-search/blob/main/finderapp/src/search/resultsPanel/ResultPanel.tsx ResultPanel.tsx - AWS Samples}
 * @param {Object} props - The component props.
 * @param {Array} props.widgets - The widgets to display when no query is present.
 * @returns {JSX.Element} The rendered component.
 */
const KendraSearch = ({ widgets }) => {
  const {
    search,
    setSearch,
    kendra,
    getResultsHelper,
    pushEvent
  } = useKendra()

  const {
    enableFacets,
    showGenAIResult,
    showTopResults,
    showFAQResults,
    showDocumentResults,
    enableSorting,
    enablePagination,
    enablePageIndex,
    documentResultsOptions
  } = search.resultsOptions

  /**
   * Determines the CSS class names for the results section based on whether
   * facets are shown. 9/12 cols
   * @type {string}
   */
  const resultsClassnames = enableFacets
    ? RESULTS_CLASSNAMES_WITH_FACETS
    : RESULTS_CLASSNAMES_WITHOUT_FACETS;

  /**
   * Determines the CSS class names for the facets section based on whether
   * facets are shown. 3/12 cols
   * @type {string}
   */
  const facetsClassnames = enableFacets
    ? FACETS_CLASSNAMES_WITH_FACETS
    : FACETS_CLASSNAMES_WITHOUT_FACETS;

  /**
   * Describes the Kendra index.
   *
   * @param {string} indexId - The ID of the index to describe.
   * @returns {Promise<Object|null>} The index description or null if an error occurs.
   */
  const describeIndex = async (indexId) => {
    try {
      return await kendra(API_OPERATIONS.DESCRIBE_INDEX, { indexId })
    } catch (e) {
      setSearch({ type: 'ERROR', payload: e })
      console.log('Error in index description')
    }
    return null
  }

  /**
   * Describes the query suggestions configuration for the Kendra index.
   *
   * @param {string} indexId - The ID of the index to describe.
   * @returns {Promise<Object|null>} The query suggestions configuration or null if an error occurs.
   */
  const describeQuerySuggestionsConfig = async (indexId) => {
    try {
      return await kendra(API_OPERATIONS.DESCRIBE_QUERY_SUGGESTIONS_CONFIG, {
        indexId
      })
    } catch (e) {
      setSearch({ type: 'ERROR', payload: e })
      console.log('Error in query suggestions config')
    }
    return null
  }

  /**
   * Lists the data sources for the Kendra index.
   *
   * @param {string} indexId - The ID of the index to list data sources for.
   * @returns {Promise<Array>} The list of data sources or an empty array if an error occurs.
   */
  const listDataSources = async (indexId) => {
    try {
      const listDsResponse = await kendra(API_OPERATIONS.LIST_DATA_SOURCES, {
        indexId
      })
      return listDsResponse.SummaryItems || []
    } catch (e) {
      setSearch({ type: 'ERROR', payload: e })
      console.log('Error in datasource name lookup')
    }
    return null
  }

  /**
   * Submits feedback for a search result.
   *
   * @param {string} relevance - The relevance feedback value.
   * @param {Object} resultItem - The search result item.
   * @returns {Promise<Object|null>} The feedback response or null if an error occurs.
   */
  const submitFeedback = async (relevance, resultItem) => {
    try {
      const queryResult = search.searchResults
      const feedbackRequest = {
        IndexId: search.indexId,
        QueryId: queryResult.QueryId,
        ...(relevance !== FEEDBACK.CLICK
          ? { RelevanceFeedbackItems: [{
            RelevanceValue: relevance, ResultId: resultItem.Id
          }] }
          : { ClickFeedbackItems: [{ ResultId: resultItem.Id }] })
      };
      const response = await kendra(
        API_OPERATIONS.SUBMIT_FEEDBACK,
        feedbackRequest
      )
      if (response) {
        pushEvent({
          action: 'feedback',
          question: resultItem.DocumentTitle.Text,
          link_to: resultItem.DocumentURI,
          outcome_type: 'submit',
          outcome_value: relevance
        });
        return response;
      }
      return null
    } catch (e) {
      setSearch({ type: 'ERROR', payload: e })
      console.log('Error in submitting feedback')
    }
    return null
  };


  useEffect(() => {
    let isMounted = true
    if (!search.isKendraEnabled || !search.indexId) return

    const fetchData = async () => {
      try {
        const index = await describeIndex(search.indexId)
        const dataSources = await listDataSources(search.indexId);
        const qsConfig = await describeQuerySuggestionsConfig(search.indexId);
        if (isMounted) {
          setSearch({
            type: 'BATCH_UPDATE',
            payload: {
              index,
              ...index.DocumentMetadataConfigurations && {
                availableSortingAttributes:
                  search.availableSortingAttributes.fromIndexMetadata(
                    index.DocumentMetadataConfigurations
                  )
              },
              dataSourceNameLookup: getDataSourceNameLookup(dataSources),
              querySuggestionsConfig: qsConfig?.Mode === QUERY_SUGGESTIONS_MODE.ENABLED,
            }
          })
        }
      } catch (e) {
        if (isMounted) {
          setSearch({ type: 'ERROR', payload: e })
          setSearch({ type: 'DATA_READY', payload: false })
          console.log('Error in index description', e)
        }
      }
    }
    fetchData()
    return () => {
      isMounted = false
    }
  }, [search.isKendraEnabled, search.indexId])

  /**
   * Computes the filter and resubmits the query.
   *
   * @param {Object} params - The parameters for the query.
   * @param {string} params.queryText - The query text.
   * @param {number} [params.pageNumber=1] - The page number.
   * @returns {Promise<void>}
   */
  const computeFilterAndReSubmitQuery = ({ queryText, pageNumber = 1 }) => {
    return getResultsHelper({ queryText, pageNumber })
  }

  /**
   * Gets results when the page changes.
   *
   * @param {string} queryText - The query text.
   * @param {number} [pageNumber=1] - The page number.
   * @returns {Promise<void>}
   */
  const getResultsOnPageChanging = async (queryText, pageNumber = 1) => {
    await computeFilterAndReSubmitQuery({ queryText, pageNumber })
  }

  const { queryText, dataReady } = search

  return (
    <KendraStyledWrapper documentResultsOptions={documentResultsOptions}>
      {queryText && dataReady && (
        <div className="search-container">
          <Row>

            {/* @todo Facets go here */}

            <div className={resultsClassnames}>
              {search.searchResults.TotalNumberOfResults === 0 && (
                <div className="empty-results center-align">
                  No results found.
                </div>
              )}
              {search.searchResults.TotalNumberOfResults !== 0 && (
                <div className="results-section">
                  <div className="results-header">
                    {enablePageIndex && (
                      <div className="results-number">
                        <PageIndex
                          dataReady={search.dataReady}
                          results={search.searchResults}
                          currentPageNumber={search.currentPageNumber}
                        />
                      </div>
                    )}
                    {enableSorting && (
                      <ResultSorting
                        availableSortingAttributes={
                          search.availableSortingAttributes
                        }
                        selectedSortingAttribute={
                          search.selectedSortingAttribute
                        }
                      />
                    )}
                  </div>
                  <ResultPanel
                    queryText={search.queryText}
                    extendedResults={search.extendedResults}
                    results={search.searchResults}
                    topResults={search.topResults}
                    faqResults={search.faqResults}
                    docResults={search.docResults}
                    dataReady={search.dataReady}
                    selectedFacets={search.selectedFacets}
                    currentPageNumber={search.currentPageNumber}
                    submitFeedback={submitFeedback}
                    showGenAIResult={showGenAIResult}
                    showTopResults={showTopResults}
                    showFAQResults={showFAQResults}
                    showDocumentResults={showDocumentResults}
                  />
                  {enablePagination && (
                    <Pagination
                      queryText={search.queryText}
                      currentPageNumber={search.currentPageNumber}
                      onSubmit={getResultsOnPageChanging}
                      results={search.searchResults}
                    />
                  )}
                </div>
              )}
            </div>
          </Row>
        </div>
      )}
      {!queryText && !dataReady && <Row>{widgets}</Row>}
      {queryText && !dataReady && (
        <div className="results-section center-align">
          <InlineLoader />
        </div>
      )}
    </KendraStyledWrapper>
  )
}

export default KendraSearch
